//core
import React, {useEffect, useState} from "react";
import arePropsEqual from "react-fast-compare";
import {makeStyles} from "@mui/styles";
import {useTranslation} from "react-i18next";
import {format, parse} from "date-fns";
import enLocale from "date-fns/locale/en-US";
import ruLocale from "date-fns/locale/ru";
import ukLocale from "date-fns/locale/uk";
import esLocale from "date-fns/locale/es";
import trLocale from "date-fns/locale/tr";
import koLocale from "date-fns/locale/ko";
import thLocale from "date-fns/locale/th";
import viLocale from "date-fns/locale/vi";
import idLocale from "date-fns/locale/id";
import zhLocale from "date-fns/locale/zh-CN";

interface IDateTime {
  value: any;
  fullDate?: boolean;
  hideSeconds?: boolean;
}

type itemLocale = {
  locale: object,
  format: string,
  withOutSeconds: string,
}

const localeMap = {
  en: {
    locale: enLocale,
    format: 'MM/dd/yyyy',
    withOutSeconds: 'MM/dd/yyyy',
  },
  ru: {
    locale: ruLocale,
    format: 'dd.MM.yyyy',
    withOutSeconds: 'dd.MM.yyyy',
  },
  uk: {
    locale: ukLocale,
    format: 'dd.MM.yyyy',
    withOutSeconds: 'dd.MM.yyyy',
  },
  es: {
    locale: esLocale,
    format: 'dd/MM/yyyy',
    withOutSeconds: 'dd/MM/yyyy',
  },
  tr: {
    locale: trLocale,
    format: 'dd.MM.yyyy',
    withOutSeconds: 'dd.MM.yyyy',
  },
  ko: {
    locale: koLocale,
    format: 'yyyy.MM.dd',
    withOutSeconds: 'yyyy.MM.dd',
  },
	th: {
		locale: thLocale,
		format: 'dd/MM/yyyy',
		withOutSeconds: 'dd/MM/yyyy',
	},
	vi: {
		locale: viLocale,
		format: 'dd/MM/yyyy',
		withOutSeconds: 'dd/MM/yyyy',
	},
	id: {
		locale: idLocale,
		format: 'dd/MM/yyyy',
		withOutSeconds: 'dd/MM/yyyy',
	},
	zh: {
		locale: zhLocale,
		format: 'yyyy/MM/dd',
		withOutSeconds: 'yyyy/MM/dd',
	},
};

const fullLocaleMap = {
  en: {
    locale: enLocale,
    format: 'MM/dd/yyyy HH:mm:ss',
    withOutSeconds: 'MM/dd/yyyy HH:mm',
  },
  ru: {
    locale: ruLocale,
    format: 'dd.MM.yyyy HH:mm:ss',
    withOutSeconds: 'dd.MM.yyyy HH:mm',
  },
  uk: {
    locale: ukLocale,
    format: 'dd.MM.yyyy HH:mm:ss',
    withOutSeconds: 'dd.MM.yyyy HH:mm',
  },
  es: {
    locale: esLocale,
    format: 'dd/MM/yyyy HH:mm:ss',
    withOutSeconds: 'dd/MM/yyyy HH:mm',
  },
  tr: {
    locale: trLocale,
    format: 'dd.MM.yyyy HH:mm:ss',
    withOutSeconds: 'dd.MM.yyyy HH:mm',
  },
  ko: {
    locale: koLocale,
    format: 'yyyy.MM.dd HH:mm:ss',
    withOutSeconds: 'yyyy.MM.dd HH:mm',
  },
	th: {
		locale: thLocale,
		format: 'dd/MM/yyyy HH:mm:ss',
		withOutSeconds: 'dd/MM/yyyy HH:mm',
	},
	vi: {
		locale: viLocale,
		format: 'dd/MM/yyyy HH:mm:ss',
		withOutSeconds: 'dd/MM/yyyy HH:mm',
	},
	id: {
		locale: idLocale,
		format: 'dd/MM/yyyy HH:mm:ss',
		withOutSeconds: 'dd/MM/yyyy HH:mm',
	},
	zh: {
		locale: zhLocale,
		format: 'yyyy/MM/dd HH:mm:ss',
		withOutSeconds: 'yyyy/MM/dd HH:mm',
	},
};

const useStyles = makeStyles({
  text: {
    color: 'inherit',
    whiteSpace: 'nowrap',
  }
});

const DateTimeRange = ({
  value,
  fullDate = false,
  hideSeconds = false,
}: IDateTime) => {
  const styles = useStyles();
  const { i18n } = useTranslation();

  const val: string[] = !!value ? value.split(' - ') : [];

  const [locale, setLocale] = useState<itemLocale>(fullDate ? fullLocaleMap.en : localeMap.en);

  useEffect(() => {
    if (i18n.language === 'ru') {
      setLocale(fullDate ? fullLocaleMap.ru : localeMap.ru);
    }
    if (i18n.language === 'uk') {
      setLocale(fullDate ? fullLocaleMap.uk : localeMap.uk);
    }
    if (i18n.language === 'en') {
      setLocale(fullDate ? fullLocaleMap.en : localeMap.en);
    }
    if (i18n.language === 'es') {
      setLocale(fullDate ? fullLocaleMap.es : localeMap.es);
    }
    if (i18n.language === 'tr') {
      setLocale(fullDate ? fullLocaleMap.tr : localeMap.tr);
    }
    if (i18n.language === 'ko') {
      setLocale(fullDate ? fullLocaleMap.ko : localeMap.ko);
    }
		if (i18n.language === 'th') {
      setLocale(fullDate ? fullLocaleMap.th : localeMap.th);
    }
		if (i18n.language === 'vi') {
      setLocale(fullDate ? fullLocaleMap.vi : localeMap.vi);
    }
		if (i18n.language === 'id') {
      setLocale(fullDate ? fullLocaleMap.id : localeMap.id);
    }
		if (i18n.language === 'zh') {
      setLocale(fullDate ? fullLocaleMap.zh : localeMap.zh);
    }
  }, [i18n.language]);

  const date = !!value && val.reduce((acc, item, i) => {
    try {
      const parsedDate = parse(item, 'yyyy-MM-dd HH:mm:ss', new Date(), { locale: locale.locale });
      return acc + `${format(parsedDate, hideSeconds ? locale.withOutSeconds : locale.format, {locale: locale.locale})}${val.length !== i+1 ? ' - ' : ''}`
    } catch (e) {
      return acc + `${format(new Date(item), hideSeconds ? locale.withOutSeconds : locale.format, {locale: locale.locale})}${val.length !== i+1 ? ' - ' : ''}`
    }
  }, '');

  return (
    <span className={styles.text}>
      { value ? date : '—' }
    </span>
  );
};

export default React.memo(DateTimeRange, arePropsEqual);
